import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DownloadIcon from '@mui/icons-material/Download'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import IconButton from '@mui/material/IconButton'
import { appReleaseService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import {
  copyDownloadUrl,
  openApkDownload,
} from '../utils/appReleaseDownload'
import { formatDateTime } from '../utils/dateFormat'

const formatBytes = (bytes) => {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let size = n
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i++
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

const ReleasesListPage = () => {
  const navigate = useNavigate()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.APP_RELEASE,
    'write',
  )

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [latestDownloadLoading, setLatestDownloadLoading] = useState(false)
  const [toast, setToast] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await appReleaseService.list({
        page: page + 1,
        limit: rowsPerPage,
      })
      setItems(data.items || [])
      setTotal(data.pagination?.total || 0)
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load releases')
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage])

  useEffect(() => {
    load()
  }, [load])

  const activeRow = items.find((r) => r.is_active)

  const fetchLatestDownloadUrl = async () => {
    setLatestDownloadLoading(true)
    try {
      return await appReleaseService.getLatestDownloadUrl()
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to get latest download link')
      return null
    } finally {
      setLatestDownloadLoading(false)
    }
  }

  const handleDownloadLatest = async () => {
    const data = await fetchLatestDownloadUrl()
    if (!data?.download_url) return
    openApkDownload(data.download_url)
    setToast(
      `Download started for ${data.version_name} (build ${data.version_code})`,
    )
  }

  const handleCopyLatestLink = async () => {
    const data = await fetchLatestDownloadUrl()
    if (!data?.download_url) return
    await copyDownloadUrl(data.download_url)
    setToast(
      `Download link copied for ${data.version_name} (build ${data.version_code}).`,
    )
  }

  const handleDelete = async (e, release) => {
    e.stopPropagation()
    if (release.is_active) return
    const label = `${release.version_name} (versionCode ${release.version_code})`
    if (!window.confirm(`Delete inactive release ${label}? This cannot be undone.`)) {
      return
    }
    try {
      await appReleaseService.remove(release.id)
      setToast(`Deleted ${label}`)
      await load()
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete release')
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Releases
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {total > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={
                  latestDownloadLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownloadLatest}
                disabled={latestDownloadLoading || loading}
              >
                Download latest
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyLatestLink}
                disabled={latestDownloadLoading || loading}
              >
                Copy latest link
              </Button>
            </>
          )}
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => navigate('/releases/new')}
              sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
            >
              Upload New Release
            </Button>
          )}
        </Box>
      </Box>

      {activeRow && (
        <Alert
          icon={<CheckCircleIcon fontSize="inherit" />}
          severity="success"
          sx={{ mb: 2 }}
        >
          Active version: <b>{activeRow.version_name}</b> (versionCode{' '}
          {activeRow.version_code}) · {activeRow.update_type} update
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>versionCode</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>By</TableCell>
                {canWrite && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 8 : 7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No releases yet. Upload one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    onClick={() => navigate(`/releases/${r.id}`)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: r.is_active ? 'rgba(76, 175, 80, 0.08)' : undefined,
                    }}
                  >
                    <TableCell sx={{ fontWeight: r.is_active ? 600 : 400 }}>
                      {r.version_name}
                    </TableCell>
                    <TableCell>{r.version_code}</TableCell>
                    <TableCell>
                      <Chip
                        label={r.update_type}
                        size="small"
                        color={r.update_type === 'FORCE' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {r.is_active ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip label="Inactive" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{formatBytes(r.file_size_bytes)}</TableCell>
                    <TableCell>{formatDateTime(r.created_at)}</TableCell>
                    <TableCell>{r.created_by_admin_email || `#${r.created_by_admin_id}`}</TableCell>
                    {canWrite && (
                      <TableCell align="right">
                        {!r.is_active && (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete release"
                            onClick={(e) => handleDelete(e, r)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Card>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Box>
  )
}

export default ReleasesListPage
